import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type SidebarChromeContextValue = {
  mobileNavOpen: boolean;
  setMobileNavOpen: React.Dispatch<React.SetStateAction<boolean>>;
  closeMobileNav: () => void;
  desktopCollapsed: boolean;
  setDesktopCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  toggleDesktopCollapsed: () => void;
};

const SidebarChromeContext = createContext<SidebarChromeContextValue | null>(null);

/**
 * Holds sidebar / drawer UI state above the route tree so it survives navigations.
 * Each page renders its own `PageLayout`; without this, open/collapsed state would reset on every route change.
 */
export function SidebarChromeProvider({ children }: { children: React.ReactNode }) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);

  const closeMobileNav = useCallback(() => {
    setMobileNavOpen(false);
  }, []);

  const toggleDesktopCollapsed = useCallback(() => {
    setDesktopCollapsed((c) => !c);
  }, []);

  const value = useMemo(
    () => ({
      mobileNavOpen,
      setMobileNavOpen,
      closeMobileNav,
      desktopCollapsed,
      setDesktopCollapsed,
      toggleDesktopCollapsed,
    }),
    [mobileNavOpen, desktopCollapsed, closeMobileNav, toggleDesktopCollapsed],
  );

  return <SidebarChromeContext.Provider value={value}>{children}</SidebarChromeContext.Provider>;
}

export function useSidebarChrome(): SidebarChromeContextValue {
  const ctx = useContext(SidebarChromeContext);
  if (!ctx) {
    throw new Error('useSidebarChrome must be used within SidebarChromeProvider');
  }
  return ctx;
}
